import { memo } from "react";
import { useTranslation } from "../i18n";

// One configured slot on a Pick & Sort bay. The bay's full set of these drives
// the unloader grid; their shelf/position pair places each cell.
export type PickAndSortBaySlot = {
    shelf: string;
    position: number;
    label: string;
    shelf_colour: string | null;
    position_colour: string | null;
};

// Widest run of positions drawn as ONE row. A bay wider than this — the
// 18-position Putwall — is drawn as stacked bands of BAND_WIDTH positions,
// one per physical sub-bay (positions 1–6 are one bay of the wall, 7–12 the
// next, 13–18 the last), so the cells stay big enough to read at arm's
// length on a portrait handheld instead of shrinking to fit 18 across.
// Matches the admin grid's heavier rule after every 6th column.
export const BAND_WIDTH = 6;

// A bay-shaped grid of the bay's slots, highlighting the ones that still hold a
// sorted order to empty. Follows the picking screen's bay diagram, except that
// the columns are mirrored (see below — the bay is unloaded from the other
// side) and the grid shape follows the bay's actual settings (rows = shelves
// present, columns = positions present) rather than a fixed 5×6 — so it differs
// per warehouse/bay.
//
// `title` names the bay when a double-bay Large wave renders one grid per bay.
// On a Large wave the operator is looking at TWO identical-looking grids
// stacked, so the header also carries that bay's own outstanding count and a
// cleared bay dims itself — the operator has to be able to tell, at a glance
// and from a couple of metres away, which bay still has work in it.
const PickAndSortUnloadGrid = ({ slots, toEmpty, title, statusLabel }: {
    slots: PickAndSortBaySlot[];
    toEmpty: Set<string>;
    title?: string;
    statusLabel?: string;
}) => {
    const { t } = useTranslation();
    if (slots.length === 0) return null;
    const done = toEmpty.size === 0;

    // Rows top-to-bottom by descending shelf letter (E…A) to match the physical
    // bay and the picking diagram — which side you stand on doesn't change
    // shelf heights, so row order is the same as on the sorting screen.
    //
    // Columns run RIGHT-to-left, i.e. mirrored against the sorting screen: the
    // wave is unloaded from the far side of the bay, so the position 1 that sat
    // on the sorter's left is on the unloader's right. The grid is flipped so it
    // matches what the operator is actually looking at while emptying the shelf.
    const shelves = Array.from(new Set(slots.map(s => s.shelf))).sort().reverse();
    const maxPos = slots.reduce((m, s) => Math.max(m, s.position), 0);
    const byKey = new Map(slots.map(s => [`${s.shelf}:${s.position}`, s]));

    // Bands of BAND_WIDTH positions, stacked. The stack is mirrored too: from
    // the unloading side the wall reads 18…1 left-to-right, so the band with
    // the highest positions comes first (top) and each band's columns run
    // high-to-low. A bay no wider than one band is a single, untitled grid —
    // exactly what a normal 6-position bay has always drawn.
    const bandCount = Math.ceil(maxPos / BAND_WIDTH);
    const bands = Array.from({ length: bandCount }, (_, b) => {
        const lo = b * BAND_WIDTH + 1;
        const hi = Math.min(maxPos, lo + BAND_WIDTH - 1);
        return { lo, hi, positions: Array.from({ length: hi - lo + 1 }, (_, i) => hi - i) };
    }).reverse();
    const banded = bands.length > 1;

    return (
        <div className={`pas-bay-grid-wrap${statusLabel ? " pas-bay-grid-wrap--panel" : ""}${done && statusLabel ? " pas-bay-grid-wrap--done" : ""}`}>
            {title && (
                <div className="pas-bay-grid-title">
                    <span className="pas-bay-grid-name">{title}</span>
                    {statusLabel && (
                        <span className={`pas-bay-grid-count${done ? " pas-bay-grid-count--done" : ""}`}>{statusLabel}</span>
                    )}
                </div>
            )}
            {bands.map(band => (
                <div key={band.lo} className={`pas-bay-band${banded ? " pas-bay-band--multi" : ""}`}>
                    {banded && (
                        <div className="pas-bay-band-title">
                            {t("pas_bay_band", { from: String(band.hi), to: String(band.lo) })}
                        </div>
                    )}
                    <div className="pas-bay-grid" style={{ gridTemplateColumns: `repeat(${band.positions.length}, 1fr)` }}>
                        {shelves.flatMap(shelf =>
                            band.positions.map(pos => {
                                const slot = byKey.get(`${shelf}:${pos}`);
                                // A hole in the bay's layout — keep the cell so the grid
                                // geometry still maps to the physical bay, but render it blank.
                                if (!slot) return <span key={`${shelf}:${pos}`} className="pas-bay-cell pas-bay-cell--empty" aria-hidden="true" />;
                                const active = toEmpty.has(slot.label);
                                return (
                                    <span
                                        key={`${shelf}:${pos}`}
                                        className={`pas-bay-cell${active ? " pas-bay-cell--active" : ""}`}
                                        aria-label={active ? `Slot ${slot.label} — to empty` : `Slot ${slot.label}`}
                                    >
                                        {slot.label}
                                    </span>
                                );
                            }),
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default memo(PickAndSortUnloadGrid);
